import React from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  HStack,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { Home, Activity, BarChart, MessageCircle, Menu, Heart } from 'lucide-react';

interface MobileFrameProps {
  children: React.ReactNode;
  activeTab: string;
  onTabChange: (tab: string) => void;
}

const MobileFrame: React.FC<MobileFrameProps> = ({ 
  children, 
  activeTab,
  onTabChange 
}) => {
  // Colors
  const frameBorderColor = useColorModeValue('gray.300', 'gray.600');
  const frameBg = useColorModeValue('gray.100', 'gray.800');
  const screenBg = useColorModeValue('white', 'gray.900');
  const notchBg = useColorModeValue('gray.800', 'gray.900');
  const tabBarBg = useColorModeValue('white', 'gray.800');
  const inactiveTabColor = useColorModeValue('gray.500', 'gray.500');
  const activeTabColor = useColorModeValue('blue.500', 'blue.300');
  
  // Navigation tabs
  const tabs = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'activities', label: 'Activities', icon: Activity },
    { id: 'wellness', label: 'Wellness', icon: Heart },
    { id: 'assistant', label: 'JIMI Chat', icon: MessageCircle },
    { id: 'menu', label: 'Menu', icon: Menu },
  ];
  
  return (
    <Box
      position="relative"
      width="350px"
      height="725px"
      borderRadius="45px"
      borderWidth="15px"
      borderColor={frameBorderColor}
      bg={frameBg}
      boxShadow="xl"
      overflow="hidden"
      mx="auto"
    >
      {/* Phone notch */}
      <Box 
        position="absolute"
        top="0"
        left="50%"
        transform="translateX(-50%)"
        width="150px"
        height="30px"
        bg={notchBg}
        borderBottomRadius="15px"
        zIndex="2"
      />
      
      {/* Screen */}
      <Box
        position="relative"
        width="100%"
        height="100%"
        bg={screenBg}
        overflow="hidden"
        borderRadius="30px"
      >
        {/* Content area */}
        <Box 
          position="relative"
          height="calc(100% - 75px)"
          overflow="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '5px',
            },
            '&::-webkit-scrollbar-track': {
              width: '7px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'gray.300',
              borderRadius: '30px',
            },
          }}
        >
          {children}
        </Box>
        
        {/* Bottom tab bar */}
        <Box
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          height="75px"
          bg={tabBarBg}
          borderTopWidth="1px"
          borderTopColor={frameBorderColor}
          zIndex="1"
        >
          <HStack 
            height="100%" 
            spacing={0} 
            justify="space-around"
            align="center"
            px={2}
          >
            {tabs.map((tab) => (
              <Flex
                key={tab.id}
                as={motion.div}
                direction="column"
                align="center"
                justify="center"
                flex="1"
                height="100%"
                cursor="pointer"
                onClick={() => onTabChange(tab.id)}
                whileTap={{ scale: 0.95 }}
                py={2}
                px={1}
                role="button"
                aria-label={`${tab.label} tab`}
              >
                <VStack spacing={1}>
                  <Icon
                    as={tab.icon}
                    boxSize={5}
                    color={activeTab === tab.id ? activeTabColor : inactiveTabColor}
                  />
                  <Text
                    fontSize="xs"
                    fontWeight={activeTab === tab.id ? "medium" : "normal"}
                    color={activeTab === tab.id ? activeTabColor : inactiveTabColor}
                    textAlign="center"
                    lineHeight="1"
                    noOfLines={1}
                    pointerEvents="none"
                  >
                    {tab.label}
                  </Text>
                </VStack>
              </Flex>
            ))}
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileFrame;
